import { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { forEach } from "lodash";
import { useNavigate } from "react-router-dom";
import { empty, errorResponse, isObject, prepareResponseData } from "../../Utilities/utils";
import { Toast } from "primereact/toast";

// css
import "../students/Students.css";

// api
import schoolApi from "../../api/School";

// data
import { yesNo } from "../../data/yesNo";
import { NigeriaStatesAndLGA } from "../../data/NigeriaStatesAndLGA";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import SelectField from "../../components/form/SelectField";
import Underline from "../../components/others/Underline";
import FullPageLoader from "../../components/loader/FullPageLoader";
import InputField from "../../components/form/InputField";
import { AuthContext } from "../Root/ProtectedRoute";
import SettingsWrapper from "../../components/appWrapper/SettingsWrapper";

const required = "This field is required!";
const validationSchema = Yup.object().shape({
  name: Yup.string().required(required),
  owner_name: Yup.string().required(required),
  owner_phone: Yup.string().required(required),
  address: Yup.string().required(required),
  state: Yup.string().required(required),
  lga: Yup.string().required(required),
  state_of_residence: Yup.string().required(required),
  lga_of_residence: Yup.string().required(required),
  state_of_origin: Yup.string().required(required),
  lga_of_origin: Yup.string().required(required),
  owner_title: Yup.string().required(required),
  owner_email: Yup.string().email("Invalid email").required(required),
  phone_number1: Yup.string().required(required),
  owner_address: Yup.string().required(required),
  phone_number2: Yup.string().optional(),
  govt_approved: Yup.string().required(required),
});

const UpdateProfile = ({ ...props }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
  const [isLoading, setIsLoading] = useState(false);
  const toastTR = useRef(null);

  // states and LGAs
  const [lgaOfOrigin, setLgaOfOrigin] = useState([]);
  const [lgasOfResidence, setLgasOfResidence] = useState([]);
  const [lgaLocated, setLgaLocated] = useState([]);

  const [initialValues, setInitialValues] = useState({
    name: !empty(user) && !empty(user.name) ? user.name : "",
    owner_name: !empty(user) && !empty(user.ownerName) ? user.ownerName : "",
    owner_email: !empty(user) && !empty(user.ownerEmail) ? user.ownerEmail : "",
    owner_phone: !empty(user) && !empty(user.ownerPhone) ? user.ownerPhone : "",
    address: !empty(user) && !empty(user.address) ? user.address : "",
    state: !empty(user) && !empty(user.state) ? user.state : "",
    lga: !empty(user) && !empty(user.lga) ? user.lga : "",
    owner_title: !empty(user) && !empty(user.ownerTitle) ? user.ownerTitle : "",
    state_of_residence:
      !empty(user) && !empty(user.stateOfResidence)
        ? user.stateOfResidence
        : "",
    lga_of_residence:
      !empty(user) && !empty(user.lgaOfResidence) ? user.lgaOfResidence : "",
    state_of_origin:
      !empty(user) && !empty(user.stateOfOrigin) ? user.stateOfOrigin : "",
    lga_of_origin:
      !empty(user) && !empty(user.lgaOfOrigin) ? user.lgaOfOrigin : "",
    owner_address:
      !empty(user) && !empty(user.ownerAddress) ? user.ownerAddress : "",
    phone_number1:
      !empty(user) && !empty(user.phoneNumber1) ? user.phoneNumber1 : "",
    phone_number2:
      !empty(user) && !empty(user.phoneNumber2) ? user.phoneNumber2 : "",
    govt_approved:
      !empty(user) && !empty(user.govtApproved) ? user.govtApproved : "No",
  });

  useEffect(() => {
    const stateOfOrigin = !empty(user.stateOfOrigin) ? user.stateOfOrigin : "";
    const stateLocated = !empty(user.state) ? user.state : "";
    const stateOfResidence = !empty(user.stateOfResidence)
      ? user.stateOfResidence
      : "";
    const stateOfOiginLGAs =
      NigeriaStatesAndLGA.find(
        (stateObj) =>
          !empty(stateObj) &&
          !empty(stateObj.title) &&
          stateObj.title === stateOfOrigin
      )?.lgas || [];
    setLgaOfOrigin(stateOfOiginLGAs);
    const stateLocatedLGAs =
      NigeriaStatesAndLGA.find(
        (stateObj) =>
          !empty(stateObj) &&
          !empty(stateObj.title) &&
          stateObj.title === stateLocated
      )?.lgas || [];
    setLgaLocated(stateLocatedLGAs);
    const stateOfResidenceLGAs =
      NigeriaStatesAndLGA.find(
        (stateObj) =>
          !empty(stateObj) &&
          !empty(stateObj.title) &&
          stateObj.title === stateOfResidence
      )?.lgas || [];
    setLgasOfResidence(stateOfResidenceLGAs);
  }, []);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  const handleStateChange = (setFieldValue, field) => (e) => {
    const selectedState = e.target.value;
    const lgas =
      NigeriaStatesAndLGA.find(
        (stateObj) =>
          !empty(stateObj) &&
          !empty(stateObj.title) &&
          stateObj.title === selectedState
      )?.lgas || [];
    setFieldValue(field, selectedState);
    if (field === "state") {
      setLgaLocated(lgas);
    } else if (field === "state_of_origin") {
      setLgaOfOrigin(lgas);
    } else if (field === "state_of_residence") {
      setLgasOfResidence(lgas);
    }
  };

  const handleSubmit = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Error Alert",
          "Something went wrong! Try again later."
        );
      }

      const fields = [
        "name",
        "owner_name",
        "owner_email",
        "address",
        "owner_phone",
        "state",
        "lga",
        "owner_title",
        "state_of_origin",
        "lga_of_origin",
        "state_of_residence",
        "lga_of_residence",
        "owner_address",
        "phone_number1",
        "phone_number2",
        "govt_approved",
      ];

      const schoolDetails = {};
      if (!empty(values)) {
        forEach(fields, (data) => {
          schoolDetails[data] =
            !empty(data) && !empty(values[data]) ? values[data] : "";
        });
      }
      let response;
      response = await schoolApi.updateProfile(schoolId, schoolDetails);
      const response_data = prepareResponseData(response);
      if (isObject(response_data) && !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Something went wrong!"
        );
      }

      if (isObject(response_data) && response_data.success) {
        return navigate(`/settings/profile`, {
          state: {
            schoolUpdated: true,
          },
        });
      }

      navigate("/settings/profile", {
        state: { schoolUpdated: false },
      });
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SettingsWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            <MainHeader title={"Update Profile"} />
            <div className="form-container mt-30">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, setFieldValue }) => (
                  <Form style={{ width: "100%" }}>
                    <div className="form-section-title">
                      School Information
                      <Underline />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Name"
                        name="name"
                        height={50}
                        labelTitle="Name"
                        required={true}
                      />
                    </div>
                    <div className="app_input_group">
                      <SelectField
                        labelTitle="State"
                        required={true}
                        placeholder="Select State"
                        name="state"
                        options={NigeriaStatesAndLGA}
                        height={50}
                        valueKey="title"
                        selectedOption={values.state}
                        handleChangeFunc={handleStateChange(
                          setFieldValue,
                          "state"
                        )}
                      />
                      <SelectField
                        labelTitle="L.G.A"
                        required={true}
                        placeholder="Select L.G.A"
                        name="lga"
                        options={lgaLocated}
                        height={50}
                        valueKey="title"
                        selectedOption={values.lga}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="form-section-title mt-40">
                      Contact Information
                      <Underline />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Phone Number 1"
                        name="phone_number1"
                        height={50}
                        required={true}
                        labelTitle="Phone Number 1"
                      />
                      <InputField
                        placeholder="Enter Phone Number 2"
                        name="phone_number2"
                        height={50}
                        labelTitle="Phone Number 2"
                      />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Address"
                        name="address"
                        height={50}
                        labelTitle="Address"
                        required={true}
                      />
                      <SelectField
                        labelTitle="Government Approved"
                        required={true}
                        placeholder="Government Approved?"
                        name="active"
                        options={yesNo}
                        height={50}
                        valueKey="title"
                        selectedOption={values.govt_approved}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="form-section-title mt-40">
                      Owner Information
                      <Underline />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Title"
                        name="owner_title"
                        height={50}
                        required={true}
                        labelTitle="Title"
                      />
                      <InputField
                        placeholder="Enter Name"
                        name="owner_name"
                        height={50}
                        required={true}
                        labelTitle="Name"
                      />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Email"
                        name="owner_email"
                        type="email"
                        height={50}
                        required={true}
                        labelTitle="Email"
                      />
                      <InputField
                        placeholder="Enter Phone Number"
                        name="owner_phone"
                        height={50}
                        required={true}
                        labelTitle="Phone Number"
                      />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Address"
                        name="owner_address"
                        height={50}
                        required={true}
                        labelTitle="Address"
                      />
                    </div>
                    <div className="app_input_group">
                      <SelectField
                        labelTitle="State of Origin"
                        placeholder="Select State of Origin"
                        name="state_of_origin"
                        options={NigeriaStatesAndLGA}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.state_of_origin}
                        handleChangeFunc={handleStateChange(
                          setFieldValue,
                          "state_of_origin"
                        )}
                      />
                      <SelectField
                        labelTitle="L.G.A of Origin"
                        placeholder="Select L.G.A of Origin"
                        name="lga_of_origin"
                        options={lgaOfOrigin}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.lga_of_origin}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="app_input_group">
                      <SelectField
                        labelTitle="State of Residence"
                        placeholder="Select State of Residence"
                        name="state_of_residence"
                        options={NigeriaStatesAndLGA}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.state_of_residence}
                        handleChangeFunc={handleStateChange(
                          setFieldValue,
                          "state_of_residence"
                        )}
                      />
                      <SelectField
                        labelTitle="L.G.A of Residence"
                        placeholder="Select L.G.A of Residence"
                        name="lga_of_residence"
                        options={lgasOfResidence}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.lga_of_residence}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="form-button-container">
                      <ButtonIcon
                        height={45}
                        marginTop={5}
                        color="#ffffff"
                        backgroundColor="#633ccd"
                        width={300}
                        borderColor="#633ccd"
                        buttonText="Update Profile"
                        type="submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </SettingsWrapper>
    </>
  );
};

export default UpdateProfile;
